import { type ValueOf } from '@dni-types/utilities/value-of'
import { type AppliedCoupon, AppliedStatus, type CartValidationMessages, type Coupon, CouponExceptionCode, type CouponType, type SelectedCouponGiveaway, type SelectedCouponGiveawayProduct } from '@hooks-api/types/dataModel/coupon'
import { type ECommerceProduct } from '@hooks-api/types/dataModel/products'
import { type ShippingMethod, ShippingMethodMap } from '@hooks-api/types/dataModel/shipping'
import { type Nullable } from '@hooks-api/types/nullable'
import { findLastIndex } from '@utils/findLastIndex'
import { formatPrice } from '@utils/formatCurrency'
import { parseToNumber } from '@utils/parseToNumber'
import { sumOfArray } from '@utils/sumOfArray'
import { parseAttributesToString } from '../parser/parseAttributes'
import { parseProductMap } from '../parser/parseProductMap'
import { type MergedClaimsCoupon, type CartItem, type CouponBaseStore, type FastpassOrderItemBySku, type GiveawayProps as GiveawaySelectProps, type GiveawayValuesWithValidateStockItem, type MergedCoupon, type MergedCouponCriterion, type MergedCouponCriterionWithCoupon, type MergedCouponMap, type ProductMap, type RegularGiveawayProps as RegularGiveawaySelectProps, type SelectedCouponGiveawayMap, type SelectedCouponGiveawayProductMap, type MergedClaimsCouponCriterionWithCoupon, type RegularGiveawayStatus } from '../types'
import { getExchangeVoucherSelectedGiveaways } from '../utils/exchange-voucher-rules'

/**
 * Helper function
 * 取得庫存數量
 * 1. stock 有可能為負數，如果為負數，則設為 0
 * 2. manage_stock 為 false 代表沒有庫存限制，設為最大值，ex: 汪喵幣
 */
export const getStock = (stock: ECommerceProduct['stock'], is_out_of_stock: ECommerceProduct['is_out_of_stock'], manage_stock: ECommerceProduct['manage_stock']) =>
  !manage_stock
    ? Number.MAX_SAFE_INTEGER
    : is_out_of_stock
      ? 0
      : Math.max(0, Number(stock) || 0) // 如果 stock 不是數字，則設為 0

/**
 * Helper function for get valid gift selection count
 * 1. 全贈贈品數量依照該條件全部的贈品數量，且庫存數量至少要 1 件
 * 2. 非全贈贈品則和庫存比較，不能超過庫存數量
 */
export const getTotalAvailableGiveaway = (giveaways: ECommerceProduct[]) => giveaways.filter(giveaway => getStock(giveaway.stock, giveaway.is_out_of_stock, giveaway.manage_stock) >= 1).length
export const getAvailableGiveawaysTotalStock = (giveaways: ECommerceProduct[]) => giveaways.reduce((acc, giveaway) => acc + getStock(giveaway.stock, giveaway.is_out_of_stock, giveaway.manage_stock), 0)
export const getLimitedGiftSelectionCount = (
  totalAvailableGiveaway: number,
  availableGiveawaysTotalStock: number,
  giftSelectionCount: number,
  isGiveAll: boolean,
) => isGiveAll ? totalAvailableGiveaway : Math.min(giftSelectionCount, availableGiveawaysTotalStock)

/**
 * Helper function
 * 取得累贈贈品可選次數
 * 1. accumulatedCount 為累贈次數，giftSelectionCount 為每次可選次數
 * 2. 如果有 accumulatedCount(不為 0 或 undefined) 則乘以 giftSelectionCount
 */
export const getAccumulatedGiftSelectionCount = (giftSelectionCount: number, criterionCount?: Nullable<number>) => {
  const accumulatedGiftSelectionCount = criterionCount ? criterionCount * giftSelectionCount : giftSelectionCount
  return accumulatedGiftSelectionCount
}

/**
 * Helper function
 * 判斷庫存是否部分不足
 */
export const getIsStockPartiallyNotEnough = (accumulatedGiftSelectionCount: number, totalGiveawayStock: number) => totalGiveawayStock < accumulatedGiftSelectionCount

const getGiveawayValuesWithValidateStock = (
  giveawayValues: SelectedCouponGiveawayProductMap | undefined,
  productMap: ProductMap | undefined,
): GiveawayValuesWithValidateStockItem[] =>
  giveawayValues
    ? Object.values(giveawayValues).map((giveawayValue) => {
      const product = productMap?.[giveawayValue.product_id]
      return {
        ...giveawayValue,
        stock: product ? getStock(product.stock, product.is_out_of_stock, product.manage_stock) : undefined,
      }
    })
    : []

export const getGiveawaySelectProps = (coupon: MergedCoupon | MergedClaimsCoupon, criterion: MergedCouponCriterion, selectedCouponGiveawayMap: Partial<Record<number, SelectedCouponGiveawayProductMap>>): GiveawaySelectProps => {
  const couponCriterionId = criterion.id
  const isRepeatable = coupon.is_repeatable
  const title = criterion.display_name
  const options = criterion.giveaways
  const isGiveAll = criterion.is_give_all
  const criterionCount = criterion.count

  const totalAvailableGiveaway = getTotalAvailableGiveaway(criterion.giveaways)
  const availableGiveawaysTotalStock = getAvailableGiveawaysTotalStock(criterion.giveaways)
  const limitedGiftSelectionCount = getLimitedGiftSelectionCount(
    totalAvailableGiveaway,
    availableGiveawaysTotalStock,
    criterion.gift_selection_count ?? 0,
    isGiveAll,
  )
  const accumulatedGiftSelectionCount = getAccumulatedGiftSelectionCount(limitedGiftSelectionCount, criterionCount)
  const isStockPartiallyNotEnough = getIsStockPartiallyNotEnough(accumulatedGiftSelectionCount, availableGiveawaysTotalStock)

  const defaultGiveawayValues = selectedCouponGiveawayMap[criterion.id]
  const totalSelected = sumOfArray(Object.values(defaultGiveawayValues || {}), (acc, cur) => acc + cur.quantity)
  /**
   * For 庫存驗證使用
   */
  const giveawaysProductMap = parseProductMap(criterion.giveaways)
  const giveawayValuesWithValidateStock = getGiveawayValuesWithValidateStock(defaultGiveawayValues, giveawaysProductMap)

  return {
    couponCriterionId,
    isRepeatable,
    title,
    options,
    isGiveAll,
    isStockPartiallyNotEnough,
    accumulatedGiftSelectionCount,
    criterionCount,
    defaultGiveawayValues,
    totalSelected,
    giveawayValuesWithValidateStock,
  }
}

export const getGiveawayShippingConflictProductIds = (
  giveawayCriteria: MergedCouponCriterion[],
  selectedCouponGiveawayMap: Partial<Record<number, SelectedCouponGiveawayProductMap>>,
  excludedShippingClass?: string[],
): Set<string> => {
  if (!excludedShippingClass?.length) return new Set<string>()
  return giveawayCriteria.reduce<Set<string>>((conflictProductIds, criterion) => {
    const selectedGiveawayMap = selectedCouponGiveawayMap[criterion.id]
    if (!selectedGiveawayMap) return conflictProductIds
    const selectedProductIds = new Set(
      Object.values(selectedGiveawayMap)
        .filter(selectedGiveaway => selectedGiveaway.quantity > 0)
        .map(selectedGiveaway => selectedGiveaway.product_id),
    )
    criterion.giveaways.forEach((giveaway) => {
      if (selectedProductIds.has(giveaway.id) && excludedShippingClass.includes(giveaway.shipping_class || '')) {
        conflictProductIds.add(giveaway.id)
      }
    })
    return conflictProductIds
  }, new Set<string>())
}

export const getGiveawayShippingConflict = (
  appliedShippingMethod: ShippingMethod | undefined,
  giveawayCriteria: MergedCouponCriterion[],
  selectedCouponGiveawayMap: Partial<Record<number, SelectedCouponGiveawayProductMap>>,
): { conflictProductIds: Set<string>, shippingMethodName: string } => {
  const excludedShippingClass = appliedShippingMethod?.shipping_rules.normal?.[0]?.excluded_shipping_class
  const conflictProductIds = getGiveawayShippingConflictProductIds(giveawayCriteria, selectedCouponGiveawayMap, excludedShippingClass)
  const shippingMethodName = conflictProductIds.size > 0 && appliedShippingMethod
    ? ShippingMethodMap[appliedShippingMethod.shipping_method_type] ?? ''
    : ''
  return { conflictProductIds, shippingMethodName }
}

export const isAllGiveawayUnavailable = (giveaways: ECommerceProduct[]) =>
  giveaways.every(giveaway => getStock(giveaway.stock, giveaway.is_out_of_stock, giveaway.manage_stock) <= 0)

export const GiveawayStockStatus = {
  AVAILABLE: 'available',
  PARTIALLY_SOLD_OUT: 'partiallySoldOut',
  ALL_SOLD_OUT: 'allSoldOut',
} as const

export type GiveawayStockStatusType = ValueOf<typeof GiveawayStockStatus>

/**
 * 依贈品庫存判斷商品小卡優惠列要顯示的贈完標籤狀態
 */
export const getGiveawayStockStatus = (giveaways: ECommerceProduct[]): GiveawayStockStatusType => {
  // 非贈品類（giveaways 為空）不顯示標籤；也避免 isAllGiveawayUnavailable 對空陣列 vacuously true 誤判為全贈完
  if (giveaways.length === 0) return GiveawayStockStatus.AVAILABLE
  if (isAllGiveawayUnavailable(giveaways)) return GiveawayStockStatus.ALL_SOLD_OUT
  const hasSoldOutGiveaway = giveaways.some(giveaway => getStock(giveaway.stock, giveaway.is_out_of_stock, giveaway.manage_stock) <= 0)
  return hasSoldOutGiveaway ? GiveawayStockStatus.PARTIALLY_SOLD_OUT : GiveawayStockStatus.AVAILABLE
}

/**
 * 優惠列前綴「已符合」判斷。贈品全贈完時 BE 可能回 INVISIBLE，但門檻已達仍應視為已符合；
 * 贈品尚有庫存的 INVISIBLE 是 is_check_top_down 壓下的低階門檻，不可認列。
 */
export const getIsCouponCriterionApplied = (criterion: MergedCouponCriterion): boolean => {
  if (criterion.applied_status === AppliedStatus.APPLIED || criterion.applied_status === AppliedStatus.UNABLE) return true
  const isGiveaway = criterion.giveaways.length > 0
  const isThresholdMet = !criterion.left_amount && !criterion.left_quantity
  return isGiveaway && criterion.applied_status === AppliedStatus.INVISIBLE && isThresholdMet && isAllGiveawayUnavailable(criterion.giveaways)
}

export const getRegularGiveawaySelectProps = (criterion: MergedCouponCriterion, selectedCouponGiveawayMap: Partial<Record<number, SelectedCouponGiveawayProductMap>>): RegularGiveawaySelectProps => {
  const couponCriterionId = criterion.id
  const displayName = criterion.display_name
  const amount = criterion.criterion_amount
  const moreAmount = criterion.left_amount
  const moreQuantity = criterion.left_quantity
  const count = criterion.count
  const options = criterion.giveaways
  const isApplied = criterion.applied_status === AppliedStatus.APPLIED
  const isAllUnavailable = isAllGiveawayUnavailable(criterion.giveaways)
  const selectedCouponGiveawayProductMap = selectedCouponGiveawayMap[criterion.id] || {}
  // 常態滿額禮是選 product_id 的值，且 products 只會有一筆，因此選擇第一個值即可
  const value = Object.values(selectedCouponGiveawayProductMap)[0]
  const disabled = isAllUnavailable || !isApplied

  /**
   * For 庫存驗證使用
   */
  const productMap = parseProductMap(criterion.giveaways)
  const product = value ? productMap?.[value.product_id] : null
  const selectedRegularGiveawayStock = product ? getStock(product.stock, product.is_out_of_stock, product.manage_stock) : undefined
  return {
    couponCriterionId,
    displayName,
    amount,
    moreAmount,
    moreQuantity,
    count,
    options,
    isApplied,
    isAllUnavailable,
    value,
    disabled,
    selectedRegularGiveawayStock,
  }
}

export const getRegularGiveawayStatus = (
  isApplied: RegularGiveawaySelectProps['isApplied'],
  value: RegularGiveawaySelectProps['value'],
  isAllUnavailable: RegularGiveawaySelectProps['isAllUnavailable'],
): RegularGiveawayStatus => {
  if (!isApplied && isAllUnavailable) {
    return 'allUnavailable'
  }
  if (!isApplied && !isAllUnavailable) {
    return 'notEligible'
  }
  if (isApplied && value) {
    return 'selected'
  }
  return 'unselected'
}

export const getCartListItemProps = (
  item: CartItem,
  product: ECommerceProduct | undefined,
  orderItemFromCalculate: FastpassOrderItemBySku | undefined,
) => {
  /** 避免 stock 為負值 */
  const stock = Math.max(parseToNumber(product?.stock), 0)
  const salePrice = parseToNumber(orderItemFromCalculate?.sale_price ?? product?.sale_price)
  const addonV2SalePrice = parseToNumber(orderItemFromCalculate?.addon_v2_price ?? item.addon_v2_price)
  const productTitle = product?.name
  const originalPrice = item.original_price ?? parseToNumber(product?.original_price)
  const attributes = parseAttributesToString(product?.attributes)
  const productPageUrl = product?.product_page_url
  const productLifeLimit = item.product_limited?.life_limit ?? 0
  const maxQuantity = productLifeLimit > 0 ? productLifeLimit : stock

  return {
    stock,
    salePrice,
    productTitle,
    originalPrice,
    attributes,
    productPageUrl,
    maxQuantity,
    addonV2SalePrice,
    productLifeLimit,
  }
}

/**
 * Helper function for get merged coupon criterions by coupon ids or types
 */
export const getMergedCouponCriterions = (
  mergedCouponMap: MergedCouponMap,
  couponIds: Coupon['id'][],
  types?: CouponType[],
): MergedCouponCriterion[] => {
  let mergedCoupons = couponIds.flatMap(couponId => mergedCouponMap.get(couponId)).filter(coupon => coupon !== undefined)
  if (types) {
    mergedCoupons = mergedCoupons.filter(coupon => types.some(type => coupon.couponTypes.has(type)))
  }
  return mergedCoupons.flatMap(mergedCoupon => mergedCoupon.criterions.map(criterion => ({ ...criterion, couponId: mergedCoupon.coupon_id, is_repeatable: mergedCoupon.is_repeatable })))
}

export const addManualInputCouponIds = (prevState: CouponBaseStore, couponIds: number[]): CouponBaseStore => ({
  ...prevState,
  manualInputCouponIds: [...new Set([...prevState.manualInputCouponIds, ...couponIds])],
})

export const removeManualInputCouponIds = (prevState: CouponBaseStore, couponIds: number[]): CouponBaseStore => ({
  ...prevState,
  manualInputCouponIds: prevState.manualInputCouponIds.filter(id => !couponIds.includes(id)),
})

export const resetManualInputCouponIds = (prevState: CouponBaseStore): CouponBaseStore => ({
  ...prevState,
  manualInputCouponIds: [],
})

export const setSelectedManualCouponId = (prevState: CouponBaseStore, couponId: number | null): CouponBaseStore => ({
  ...prevState,
  selectedManualCouponId: couponId,
})

export const setSelectedClaimCouponRedeemCode = (prevState: CouponBaseStore, couponId: number | null, redeemCode?: string, isManually?: boolean): CouponBaseStore => ({
  ...prevState,
  selectedClaimCouponId: couponId,
  selectedClaimCouponRedeemCode: redeemCode,
  hasApplyManuallyRedeemedCodes: isManually ?? false,
})

/**
 * 提交已選的點數兌換券 redeem codes（可多張）。
 * 比照 setSelectedClaimCouponRedeemCode：彈窗以本地 state 操作，套用時整批提交至 store。
 * ⚠️ 不去重：同券多張票共用同一 redeem_code，同碼 ×N = 一次核銷 N 張（BE 依出現次數核銷）。
 */
export const setSelectedExchangeVoucherRedeemCodes = (prevState: CouponBaseStore, redeemCodes: string[]): CouponBaseStore => ({
  ...prevState,
  selectedExchangeVoucherRedeemCodes: [...redeemCodes],
})

/**
 * 清空所有已選點數兌換券。
 */
export const clearSelectedExchangeVoucherRedeemCodes = (prevState: CouponBaseStore): CouponBaseStore => ({
  ...prevState,
  selectedExchangeVoucherRedeemCodes: [],
})

/**
 * 同步已選兌換券兌得的贈品（唯讀，隨券固定），供建單時併入 selected_coupon_giveaways。
 *
 * 商業邏輯：兌換型優惠券（點數兌換券）的贈品是「兌換標的」本身——門檻固定為 1（一張券換一份贈品），
 * 不像一般券由使用者挑選，而是隨券綁定。但建單契約上它仍視為 selected_coupon_giveaways 的一部分，
 * BE 需據此核銷兌換贈品；缺漏會導致 payment_transaction 建單失敗（本次修復的 ERROR 主因）。
 */
export const setSelectedExchangeVoucherGiveaways = (
  prevState: CouponBaseStore,
  criteria: MergedClaimsCouponCriterionWithCoupon[],
  selectedCountByRedeemCode?: Map<string, number>,
): CouponBaseStore => ({
  ...prevState,
  selectedExchangeVoucherGiveaways: getExchangeVoucherSelectedGiveaways(criteria, selectedCountByRedeemCode),
})

/**
 * 合併送出的 redeem codes：點數兌換券（多選）+ 一般優惠券（單選），去除空值。
 * calculate 與建立付款交易共用，確保兩處邏輯一致。
 * ⚠️ 兌換券碼不去重（同碼 ×N = 核銷 N 張，calculate 回傳 applied_coupons 長度與送出一致）；
 * 一般券仍單張，且不與兌換碼重複。
 */
export const mergeRedeemCodes = (exchangeVoucherRedeemCodes: string[] = [], claimCouponRedeemCode?: string): string[] => {
  const exchangeCodes = exchangeVoucherRedeemCodes.filter((code): code is string => Boolean(code))
  if (!claimCouponRedeemCode || exchangeCodes.includes(claimCouponRedeemCode)) return exchangeCodes
  return [...exchangeCodes, claimCouponRedeemCode]
}

/**
 * SPEC:
 * 1. 當選項單筆且重複時，選擇一次即全部選擇，同時也必須保存其他的選擇
 */
export const updateSelectedGiveaways = (
  prevState: CouponBaseStore,
  couponCriterionId: SelectedCouponGiveaway['coupon_criterion_id'],
  couponGiveawayProductMap: SelectedCouponGiveawayProductMap,
): CouponBaseStore => {
  const next = [...prevState.selectedGiveaways]
  const findIndex = next.findIndex(item => item.coupon_criterion_id === couponCriterionId)
  const products = Object.values(couponGiveawayProductMap).filter(item => item.quantity > 0)
  if (findIndex === -1) {
    next.push({ coupon_criterion_id: couponCriterionId, products })
  }
  else {
    next[findIndex] = { coupon_criterion_id: couponCriterionId, products }
  }
  return {
    ...prevState,
    selectedGiveaways: next,
  }
}

export const removeSelectedGiveaways = (
  prevState: CouponBaseStore,
  couponCriterionIds: SelectedCouponGiveaway['coupon_criterion_id'] | SelectedCouponGiveaway['coupon_criterion_id'][],
): CouponBaseStore => {
  const idsToRemove = Array.isArray(couponCriterionIds) ? couponCriterionIds : [couponCriterionIds]
  const next = prevState.selectedGiveaways.filter(item => !idsToRemove.includes(item.coupon_criterion_id))
  return {
    ...prevState,
    selectedGiveaways: next,
  }
}

export const resetSelectedGiveaways = (
  prevState: CouponBaseStore,
): CouponBaseStore => {
  return {
    ...prevState,
    selectedGiveaways: [],
  }
}

export const resetSelectedRegularGiveaways = (
  prevState: CouponBaseStore,
): CouponBaseStore => {
  return {
    ...prevState,
    selectedRegularGiveaways: [],
  }
}

/**
 * SPEC:
 * 1. 滿額禮必定只會有一個選項以及數量只能為 1
 */
export const updateSelectedRegularGiveaways = (
  prevState: CouponBaseStore,
  couponCriterionId: SelectedCouponGiveaway['coupon_criterion_id'],
  product: Omit<SelectedCouponGiveawayProduct, 'quantity'>,
): CouponBaseStore => {
  const next = [...prevState.selectedRegularGiveaways]
  const findIndex = next.findIndex(item => item.coupon_criterion_id === couponCriterionId)
  if (findIndex === -1) {
    next.push({
      coupon_criterion_id: couponCriterionId,
      products: [{
        product_id: product.product_id,
        sku: product.sku,
        project_code: product.project_code,
        quantity: 1,
      }],
    })
  }
  else {
    next[findIndex] = {
      coupon_criterion_id: couponCriterionId,
      products: [{
        product_id: product.product_id,
        sku: product.sku,
        project_code: product.project_code,
        quantity: 1,
      }],
    }
  }

  return {
    ...prevState,
    selectedRegularGiveaways: next,
  }
}

/**
 * 滿額禮移除情境
 * 當用戶已達滿額禮門檻且選擇滿額禮後，用戶反悔更改商品金額，需清除資格不符的滿額禮，避免帶入結帳頁。
 */
export const removeSelectedRegularGiveaways = (
  prevState: CouponBaseStore,
  couponCriterionIds: SelectedCouponGiveaway['coupon_criterion_id'] | SelectedCouponGiveaway['coupon_criterion_id'][],
): CouponBaseStore => {
  const idsToRemove = Array.isArray(couponCriterionIds) ? couponCriterionIds : [couponCriterionIds]
  const next = prevState.selectedRegularGiveaways.filter(
    item => !idsToRemove.includes(item.coupon_criterion_id),
  )
  return {
    ...prevState,
    selectedRegularGiveaways: next,
  }
}

/**
 * 如果使用者所選的贈品/常態滿額禮已經不存在於 applied coupon 中，則移除
 */
export const clearInvalidSelectedGiveaways = (
  prevState: CouponBaseStore,
  appliedCoupons?: AppliedCoupon[],
): CouponBaseStore => {
  if (!appliedCoupons) return prevState
  const appliedCriterionIdSet = new Set(appliedCoupons.flatMap(item => item.criterions.filter(criterion => criterion.applied_status === AppliedStatus.APPLIED).map(criterion => criterion.id)))
  const nextSelectedGiveaways = [...prevState.selectedGiveaways].filter(item => appliedCriterionIdSet.has(item.coupon_criterion_id))
  const nextSelectedRegularGiveaways = [...prevState.selectedRegularGiveaways].filter(item => appliedCriterionIdSet.has(item.coupon_criterion_id))

  return {
    ...prevState,
    selectedGiveaways: nextSelectedGiveaways,
    selectedRegularGiveaways: nextSelectedRegularGiveaways,
  }
}

/**
 *  * 如果手動輸碼券已過期或尚未開始，則移除
 */
export const clearInvalidManualInputCouponIds = (
  prevState: CouponBaseStore,
  cartValidationMessages?: CartValidationMessages[],
): CouponBaseStore => {
  if (!cartValidationMessages) return prevState
  const invalidCouponIds = new Set(
    cartValidationMessages
      .filter(item => item.code === CouponExceptionCode.COUPON_EXPIRED || item.code === CouponExceptionCode.COUPON_NOT_STARTED)
      .map(item => item.message.coupon_id),
  )
  const nextManualInputCouponIds = [...prevState.manualInputCouponIds].filter(id => !invalidCouponIds.has(id))
  return {
    ...prevState,
    manualInputCouponIds: nextManualInputCouponIds,
  }
}

/**
 * 如果選中的手動輸碼券已過期或尚未開始，則清除選取
 */
export const clearInvalidSelectedManualCouponId = (
  prevState: CouponBaseStore,
  cartValidationMessages?: CartValidationMessages[],
): CouponBaseStore => {
  if (!cartValidationMessages || prevState.selectedManualCouponId === null) return prevState
  const invalidCouponIds = new Set(
    cartValidationMessages
      .filter(item => item.code === CouponExceptionCode.COUPON_EXPIRED || item.code === CouponExceptionCode.COUPON_NOT_STARTED)
      .map(item => item.message.coupon_id),
  )
  if (!invalidCouponIds.has(prevState.selectedManualCouponId)) return prevState
  return {
    ...prevState,
    selectedManualCouponId: null,
  }
}

/**
 * 贈品預選情境
 * 1. 如果贈品是全贈，則預選 -> isReadyOnly = true
 * 2. 如果贈品是單選，則預選 -> isReadyOnly = true
 * 3. 如果贈品是多選，則自選 -> isReadyOnly = false
 * 4. 如果贈品是多選且贈送數量超過庫存 -> isReadyOnly = true
 */
export const setDefaultSelectedGiveaways = (
  prevState: CouponBaseStore,
  couponCriterions: (MergedCouponCriterionWithCoupon | MergedClaimsCouponCriterionWithCoupon)[],
): CouponBaseStore => {
  if (couponCriterions.length === 0) return prevState

  const next: SelectedCouponGiveaway[] = []
  // use map to improve performance
  const prevSelectedGiveawayMap = prevState.selectedGiveaways.reduce<Record<number, SelectedCouponGiveaway & { productsQuantityMap: Record<string, number> }>>((acc, item) => {
    acc[item.coupon_criterion_id] = {
      ...item,
      productsQuantityMap: item.products.reduce(
        (acc, { product_id, quantity }) => ({ ...acc, [product_id]: quantity }), {},
      ),
    }
    return acc
  }, {})

  couponCriterions.forEach((couponCriterion) => {
    /**
     * 如果贈品是全贈 gift_selection_count API 會回傳 0 所以設定數量為 1
     */
    const giftSelectionCount = couponCriterion.is_give_all ? 1 : couponCriterion.gift_selection_count ?? 0
    const accumulatedGiftSelectionCount = getAccumulatedGiftSelectionCount(giftSelectionCount, couponCriterion.count)
    const totalStock = getAvailableGiveawaysTotalStock(couponCriterion.giveaways)
    const isOverTotalStock = accumulatedGiftSelectionCount > totalStock
    const shouldSetDefault = couponCriterion.giveaways.length === 1 || couponCriterion.is_give_all || isOverTotalStock

    const prevCriterion = prevSelectedGiveawayMap[couponCriterion.id]

    /**
     * 如果符合情境 1 或 2 則將預選贈品加入
     */
    if (shouldSetDefault) {
      next.push({
        isReadyOnly: true,
        coupon_criterion_id: couponCriterion.id,
        products: couponCriterion.giveaways.map((giveaway) => {
          const stock = getStock(giveaway.stock, giveaway.is_out_of_stock, giveaway.manage_stock)
          return {
            product_id: giveaway.id,
            sku: giveaway.sku,
            // 當需要選擇的數量大於所有商品庫存加總，將所有有庫存的商品設定為最大，否則數量不能超過庫存
            quantity: isOverTotalStock ? stock : Math.min(accumulatedGiftSelectionCount, stock),
            project_code: giveaway.project_code,
          }
        }),
      })
    }
    else {
      /**
       * 如果符合情境 3
       * 1. 當最高可選增加時會維持原先已選數量
       * 2. 當最高可選減少時，則會依照當前已選數量調整，範例如下
       * ex: 假設原先使用者選擇最高可選 8 項贈品
       *     目前已選擇的品項數量 -> [2,1,2,1,2]
       *     當最高可選剩 6 項贈品 -> [2,1,2,1,0]
       *     當最高可選剩 4 項贈品 -> [2,1,1,0,0]
       *     當最高可選剩 2 項贈品 -> [2,0,0,0,0]
       *     當最高可選剩 0 項贈品 -> [0,0,0,0,0]
       */
      if (prevCriterion) {
        let remainingAccumulatedGiftSelectionCount = getAccumulatedGiftSelectionCount(giftSelectionCount, couponCriterion.count)
        const products: SelectedCouponGiveaway['products'] = []
        couponCriterion.giveaways.forEach((giveaway) => {
          const prevQuantity = prevCriterion.productsQuantityMap[giveaway.id] ?? 0
          const quantity = Math.min(prevQuantity, remainingAccumulatedGiftSelectionCount)
          remainingAccumulatedGiftSelectionCount = Math.max(remainingAccumulatedGiftSelectionCount - prevQuantity, 0)
          products.push({
            product_id: giveaway.id,
            sku: giveaway.sku,
            quantity,
            project_code: giveaway.project_code,
          })
        })
        next.push({
          coupon_criterion_id: couponCriterion.id,
          products,
          isReadyOnly: false,
        })
      }
    }
  })
  return {
    ...prevState,
    selectedGiveaways: next,
  }
}

/**
 * 已選的贈品值，贈品或是常態滿額禮，轉換為 map 結構，增加效能
 */
export const getSelectedCouponGiveawayMap = (selectedGiveaways: SelectedCouponGiveaway[]) => {
  return selectedGiveaways
    .reduce<SelectedCouponGiveawayMap>((acc, { coupon_criterion_id, products, isReadyOnly }) => ({
      ...acc,
      [coupon_criterion_id]: products.reduce<SelectedCouponGiveawayProductMap>((acc, { product_id, sku, project_code, quantity }) => ({
        ...acc,
        [product_id]: {
          product_id,
          sku,
          quantity,
          project_code,
          isReadyOnly,
        },
      }), {}),
    }), {})
}

export function calculateCriterionProgress(coupons: MergedCoupon[]) {
  if (coupons.length === 0) {
    return {
      percentage: 0,
      reachedPercentage: 0,
      closestCriterion: undefined,
      totalCriterionCount: 0,
      totalAppliedCriterionCount: 0,
      subtractionTotal: 0,
      maxDiscountAmount: 0,
      totalGiftSelectionCount: 0,
      sortedCriterions: [],
      allClosestCriterions: [],
    }
  }
  // 將所有門檻排序，由小到大
  const sortedCriterions = coupons
    .flatMap(coupon => coupon.criterions)
    .sort((a, b) => (b.discount_amount ?? 0) - (a.discount_amount ?? 0)) // 有折錢的先排前面
    .sort((a, b) => (a.criterion_amount ?? 0) - (b.criterion_amount ?? 0)) // 依據門檻排序

  /**
   * 目前最接近的門檻，因為是排序過的，所以是找第一個 not_reached 的門檻
   * 說明：
   * 不能使用購物車當前總金額來找下一階門檻，因為優惠券可以排除商品
   * 情境舉例：
   * 滿額折門檻 4800 觸發折 300 元，排除商品 A
   * 滿額禮門檻 4800 排除商品 B
   * 購物車內同時有 A 和 B 商品，且 A 和 B 各自的金額都加到 4500
   * 此時購物車總金額就會大於門檻金額但還不會達到門檻
   */
  const closestCriterion = sortedCriterions.find(criterion => criterion.applied_status === AppliedStatus.NOT_REACHED)
  const closestCriterionAmount = closestCriterion?.criterion_amount ?? 0
  // 篩選出目前所有最接近的相同門檻，因為可能有多個門檻金額相同，所以需要篩選出來
  const allClosestCriterions = sortedCriterions.filter(criterion => criterion.criterion_amount === closestCriterionAmount)
  const totalCriterionCount = sortedCriterions.length
  /**
   * 計算已達成條件數量，但因為滿額折的門檻不會每個都是 applied，所以需要取得最後一筆被 applied 的門檻 index
   * 確保 totalAppliedCriterionCount 不會超過 totalCriterionCount 且不會小於 0
   */
  const lastAppliedCriterionIndex = findLastIndex(sortedCriterions, criterion => criterion.applied_status === AppliedStatus.APPLIED)
  const totalAppliedCriterionCount = Math.max(Math.min(lastAppliedCriterionIndex + 1, totalCriterionCount), 0)
  /**
   * 下一階門檻還差多少
   * 將篩選出的所有最接近門檻取最大值
   * 因為門檻一樣但有可能剩餘達成金額不同，可以參考上面的情境
   * 只取最大的剩餘達成金額顯示
   */
  const subtractionTotal = Math.max(...allClosestCriterions.map(criterion => (criterion.left_amount || 0)), 0)
  // 百分比不能超過 100%
  const percentage = Math.min(Math.round((totalAppliedCriterionCount / totalCriterionCount) * 100), 100)

  /**
   * 購物車滿額禮進度條百分比除了計算 applied，也需要計算 unable 的狀態
   * 計算方式為找到最後一個非 not_reached 的門檻 index
   */
  const lastReachedCriterionIndex = findLastIndex(sortedCriterions, criterion =>
    criterion.applied_status !== AppliedStatus.NOT_REACHED,
  )
  const totalReachedCriterionCount = Math.max(Math.min(lastReachedCriterionIndex + 1, totalCriterionCount), 0)
  const reachedPercentage = Math.min(Math.round((totalReachedCriterionCount / totalCriterionCount) * 100), 100)

  const maxDiscountAmount = Math.max(0, ...sortedCriterions.map(criterion => criterion.discount_amount || 0))
  const totalGiftSelectionCount = sumOfArray(sortedCriterions, (acc, cur) => acc + (cur.gift_selection_count || 0))
  return {
    /**
     * 目前 applied 的百分比
     */
    percentage,
    /**
     * 目前 reached 的百分比
     */
    reachedPercentage,
    /**
     * 目前最接近的門檻
     */
    closestCriterion,
    /**
     * 取得所有門檻數量
     */
    totalCriterionCount,
    /**
     * 已達成條件數量
     */
    totalAppliedCriterionCount,
    /**
     * 最高優惠金額
     */
    maxDiscountAmount,
    /**
     * 下一階門檻還差多少
     */
    subtractionTotal,
    /**
     * 所有和目前 closestCriterion 相同門檻的條件
     */
    allClosestCriterions,
    /**
     * 所有門檻 (已排序)
     */
    sortedCriterions,
    /**
     * 取得所有可拿到的贈品數量
     */
    totalGiftSelectionCount,
  }
}

export const getRegularGiveawayProgress = (regularGiveaways: MergedCoupon[], totalAmount = 0) => {
  const { percentage, reachedPercentage, totalCriterionCount, totalAppliedCriterionCount, subtractionTotal } = calculateCriterionProgress(regularGiveaways)
  return { percentage, reachedPercentage, totalCriterions: totalCriterionCount, currentCriterions: totalAppliedCriterionCount, subtractionTotal, totalAmount }
}

const getNoneCurrentCriterionsDescription = ({
  maxDiscountAmount = 0,
  totalGiftSelectionCount: totalGiveaway = 0,
  currency,
}: {
  maxDiscountAmount: number
  totalGiftSelectionCount: number
  currency?: string
}) => {
  if (maxDiscountAmount > 0 && totalGiveaway > 0) {
    return {
      message: '最高可享 -{{amount}} 與 {{giveaway}} 大好禮',
      value: {
        amount: formatPrice(currency, maxDiscountAmount),
        giveaway: totalGiveaway,
      },
    }
  }
  else if (maxDiscountAmount > 0) {
    return {
      message: '最高可享 -{{amount}}',
      value: {
        amount: formatPrice(currency, maxDiscountAmount),
      },
    }
  }
  else if (totalGiveaway > 0) {
    return {
      message: '最多可拿 {{giveaway}} 大好禮',
      value: {
        giveaway: totalGiveaway,
      },
    }
  }
  return {
    message: '',
  }
}

export const getProgressbarDescription = ({
  maxDiscountAmount = 0,
  totalGiftSelectionCount = 0,
  totalAppliedCriterionCount = 0,
  totalCriterionCount = 0,
  closestCriterion,
  allClosestCriterions,
  subtractionTotal = 0,
  currency,
}: {
  totalAppliedCriterionCount: number
  maxDiscountAmount: number
  totalGiftSelectionCount: number
  totalCriterionCount: number
  closestCriterion?: MergedCouponCriterion
  allClosestCriterions: MergedCouponCriterion[]
  subtractionTotal: number
  currency?: string
}) => {
  if (totalAppliedCriterionCount === totalCriterionCount) {
    return {
      message: '已拿到所有好康！',
    }
  }
  if (!closestCriterion) {
    return getNoneCurrentCriterionsDescription({ maxDiscountAmount, totalGiftSelectionCount, currency })
  }
  if (closestCriterion.discount_amount && allClosestCriterions.some(criterion => criterion.gift_selection_count)) {
    return {
      message: '再消費 {{amount}}，享 -{{discountAmount}} 與好禮',
      value: {
        amount: `$${subtractionTotal.toLocaleString()}`,
        discountAmount: `$${closestCriterion.discount_amount.toLocaleString()}`,
      },
    }
  }
  if (closestCriterion.discount_amount) {
    return {
      message: '再消費 {{amount}}，享 -{{discountAmount}}',
      value: {
        amount: `$${subtractionTotal.toLocaleString()}`,
        discountAmount: `$${closestCriterion.discount_amount.toLocaleString()}`,
      },
    }
  }
  if (closestCriterion.gift_selection_count) {
    return {
      message: '再消費 {{amount}}，拿好禮',
      value: {
        amount: `$${subtractionTotal.toLocaleString()}`,
      },
    }
  }
  return {
    message: '',
  }
}

/**
 * 計算目前贈品可選數量
 * 1. 贈品庫存數量
 * 2. 贈品可選數量 - 已選數量，但要排除自己所以 + 回 selfSelectedQuantity
 * 3. 全贈情況下，可選數量即是 accumulatedCount
 * Note: max 是每個選項各自分開的用來判斷時要考慮到自己已選的數量
 */
export const getGiveawayProductMaxSelectableQuantity = ({
  stock,
  accumulatedGiftSelectionCount,
  totalSelectedQuantity,
  selfSelectedQuantity,
}: {
  stock: number
  /**
   * 該優惠券總可選數量
   */
  accumulatedGiftSelectionCount: number
  /**
   * 該優惠券總已選數量
   */
  totalSelectedQuantity: number
  /**
   * 目前已選數量
   */
  selfSelectedQuantity: number
}) => {
  const maxSelectableQuantity = Math.min(
    stock,
    accumulatedGiftSelectionCount - totalSelectedQuantity + selfSelectedQuantity,
  )
  return maxSelectableQuantity
}

export const getGiveawayNumberInputProps = ({
  option,
  selectedCouponGiveawayProductMap,
  isGiveAll,
  accumulatedGiftSelectionCount,
  totalSelectedQuantity,
}: {
  option: ECommerceProduct
  selectedCouponGiveawayProductMap: SelectedCouponGiveawayProductMap
  isGiveAll: boolean
  accumulatedGiftSelectionCount: number
  totalSelectedQuantity: number
}) => {
  const stock = getStock(option.stock, option.is_out_of_stock, option.manage_stock)
  const selfSelectedQuantity = selectedCouponGiveawayProductMap[option.id]?.quantity ?? 0
  const maxSelectableQuantity = Math.min(
    stock,
    accumulatedGiftSelectionCount - totalSelectedQuantity + selfSelectedQuantity,
  )

  /**
   * 是否禁用情境
   * 1. 贈品為全贈時
   * 2. 庫存為 0 / 全部可選數量已達上限 (因為 maxSelectableQuantity 已經考慮到庫存所以可以直接判斷 === 0)
   * 3. 只有一個贈品選項時 SD-2470
   */
  const disabled = isGiveAll || maxSelectableQuantity === 0
  return {
    stock,
    maxSelectableQuantity,
    selfSelectedQuantity,
    disabled,
  }
}

export const getAllSelectedGiveawayIds = (state: CouponBaseStore) => {
  const selectedGiveawayIds = state.selectedGiveaways.flatMap(
    giveaway => giveaway.products
      .filter(product => product.quantity > 0)
      .map(product => product.product_id))
  const selectedRegularGiveawayIds = state.selectedRegularGiveaways.flatMap(
    giveaway => giveaway.products
      .filter(product => product.quantity > 0)
      .map(product => product.product_id))
  const selectedExchangeVoucherGiveawayIds = state.selectedExchangeVoucherGiveaways.flatMap(
    giveaway => giveaway.products
      .filter(product => product.quantity > 0)
      .map(product => product.product_id))
  return [...selectedGiveawayIds, ...selectedRegularGiveawayIds, ...selectedExchangeVoucherGiveawayIds]
}
